<script setup lang="ts">
import { defineStore } from 'pinia';
import { editorAPI } from './api';

type Callback = () => void;

const model = defineModel<string>();
const props = defineProps<{
    src: string;
    id: string;
    name: string;
    disabled: boolean;
    init: Record<string, any>;
}>();
const textarea = ref<HTMLTextAreaElement | null>(null);
let instance: any = null;

const useStore = defineStore('wysiwyg', () =>
{
    const id = 'wysiwyg';
    const loaded = ref<boolean>(false);
    const callbacks = ref<Callback[]>([]);

    return { id, loaded, callbacks };
});

const store = useStore();

function injectScript(url: string, callback: Callback)
{
    const tag = document.createElement('script');

    tag.referrerPolicy = 'origin';
    tag.type = 'application/javascript';
    tag.id = store.id;
    tag.src = url;

    const handler = () =>
    {
        tag.removeEventListener('load', handler);

        callback();
    };

    tag.addEventListener('load', handler);

    if (document.head)
    {
        document.head.appendChild(tag);
    }
}

function initWrapper()
{
    const init = {
        ...props.init,
        readonly: props.disabled,
        target: textarea.value,
        setup: (editor: any) =>
        {
            instance = editor;

            if (typeof props.init.setup === 'function')
            {
                props.init.setup(editor);
            }

            editor.on('init', () =>
            {
                editor.setContent(model.value);
            });

            editor.on('change input undo redo', () =>
            {
                model.value = editor.getContent();
            });
        }
    };

    textarea.value.style.visibility = '';

    editorAPI().init(init);
}

watch(() => props.disabled, (disable) =>
{
    if (instance !== null)
    {
        if (typeof instance.mode?.set === 'function')
        {
            instance.mode.set(disable ? 'readonly' : 'design');
        }
        else
        {
            instance.setMode(disable ? 'readonly' : 'design');
        }
    }
});

watch(model, (value) =>
{
    if (instance != null && typeof value === 'string' && value !== instance.getContent())
    {
        instance.setContent(value);
    }
});

onMounted(() =>
{
    if (store.loaded)
    {
        initWrapper();
    }
    else
    {
        store.callbacks.push(initWrapper);

        if (!document.getElementById(store.id))
        {
            injectScript(props.src, () =>
            {
                store.callbacks.forEach(callback => callback());
                store.loaded = true;
            });
        }
    }
});

onBeforeUnmount(() =>
{
    if (editorAPI() !== null)
    {
        editorAPI().remove(instance);
    }
});
</script>

<template>
    <textarea :id="props.id" ref="textarea" :name="props.name" visibility="hidden" />
</template>
